var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { Button, Link, LoadingSpinner, Text, TextInput } from '@paypalcorp/pp-react';
import { getProp } from '../common/utils';
import { OTP_CODE, PASSWORD, PUBLIC_CREDENTIAL, SEND_OTP } from '../common/views';
/**
This page is referred to as the AAE page in XO & Identity discussions
AAE: Account Already Exists
 */
export default ({ applePayButtonSeen, authEventHandler, csrf, csrfNonce, options = {}, localizedContent = () => { }, logger, publicCredential = '', publicCredentialLookup, publicCredentialType = 'EMAIL', setIsAutoOtp, setIsAaeUser, setPublicCredential, setView, updateAfterPublicCredentialLookup, }) => {
    const [localPublicCredential, setLocalPublicCredential] = useState(publicCredentialType === 'EMAIL' ? publicCredential : '');
    const [publicCredentialFieldError, setLocalPublicCredentialFieldError] = useState('');
    const [showSpinner, setShowSpinner] = useState(false);
    useEffect(() => {
        authEventHandler({ event: 'aaePageRendered' });
        logger.track('aae_rendered');
        logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
        setIsAaeUser(false); // in case customer comes to this view a second time
    }, []);
    const handlePublicCredentialSubmit = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault && e.preventDefault();
        // Prevent proceeding in case spinner is shown (implying a network request is underway)
        if (showSpinner) {
            return;
        }
        if (localPublicCredential.trim() === '' || !localPublicCredential.match(/\S+@\S+\.\S+/)) {
            setLocalPublicCredentialFieldError(localizedContent('enterValidEmail'));
            return;
        }
        logger.track('aae_pc_submitted');
        authEventHandler({
            event: 'aaePageSubmitted',
        });
        setShowSpinner(true);
        const startTime = Date.now();
        logger.track('lookup_started');
        const response = yield publicCredentialLookup({ publicCredential: localPublicCredential });
        logger.track('lookup_completed');
        if (response === 'invalid_ctxId') {
            logger.track('gql_request_forbidden');
        }
        // XO Lite apps do not have the ngRL JS SDK loaded,
        // hence a captcha response can go unhandled
        // the following is a poor way to handle it so that XO can fallback
        const extensions = getProp(response, 'extensions', null);
        if (!extensions) {
            authEventHandler({
                event: response === 'invalid_ctxId' ? 'invalidCtxId' : 'unknownResponse',
                data: {
                    response,
                },
            });
        }
        updateAfterPublicCredentialLookup({ response, publicCredential: localPublicCredential });
        setShowSpinner(false);
        logger.track(`cpl_cit_collect_public_credential|${JSON.stringify({
            start: startTime,
            tt: Date.now() - startTime,
            login_experience: 'Process continue payment btn Click',
        })}`);
        const getLookupResponse = getProp(response, 'data.login.lookup', {});
        const { preferredLogin, otpResponse } = getLookupResponse;
        // User is not on file
        if (!Array.isArray(preferredLogin)) {
            logger.track('aae_new_user');
            logger.track(`cpl_lookup|${JSON.stringify({
                start: startTime,
                tt: Date.now() - startTime,
                login_experience: 'New user redirect',
            })}`);
            logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
            authEventHandler({
                event: 'createAccount',
                data: {
                    publicCredential: localPublicCredential,
                    publicCredentialType: 'EMAIL',
                    applePayButtonSeen,
                },
            });
            return;
        }
        setIsAaeUser(true);
        if (preferredLogin.length > 0) {
            if (preferredLogin.includes('OTP')) {
                // User is eligible for OTP
                const { challenges } = otpResponse;
                const isAutoOtp = challenges.find((challenge) => challenge.notified);
                setIsAutoOtp(isAutoOtp ? isAutoOtp.notified : false);
                logger.track(`cpl_lookup|${JSON.stringify({
                    start: startTime,
                    tt: Date.now() - startTime,
                    login_experience: `${isAutoOtp ? 'Auto sms otp login' : 'send otp'}`,
                })}`);
                setView(isAutoOtp ? OTP_CODE : SEND_OTP);
            }
        }
        else {
            logger.track(`cpl_lookup|${JSON.stringify({
                start: startTime,
                tt: Date.now() - startTime,
                login_experience: 'Hybrid Pwd Login',
            })}`);
            setView(PASSWORD);
        }
    });
    return (React.createElement(React.Fragment, null,
        showSpinner && (React.createElement("div", { className: "overlay-spinner" },
            React.createElement(LoadingSpinner, { screenReaderText: localizedContent('loading'), size: "xl" }))),
        React.createElement("form", { onSubmit: handlePublicCredentialSubmit, className: "fade-in", id: "guestPageContainer", "data-testid": "guest-page-container" },
            React.createElement(Text, { size: "xl3", className: "heading", id: "guestPageHeading", "data-testid": "guest-page-heading" }, options.signupContent && typeof localizedContent(options.signupContent) === 'string'
                ? localizedContent(options.signupContent === 'payWithCard'
                    ? 'genericHeading'
                    : 'createAccountHeading.' + options.signupContent)
                : localizedContent('createAccountHeading.createAccount')),
            React.createElement("div", null,
                React.createElement(Text, { size: "md", style: { marginBottom: '25px', fontSize: '18px' }, id: "guestPageSubHeading", "data-testid": "guest-page-sub-heading" }, localizedContent('createAccountSubHeading.createAccount')),
                React.createElement(TextInput, { autoFocus: true, name: "login_email", id: "newEmail", type: "email", autoComplete: "username", "data-testid": "input-new-email", label: localizedContent('enterEmail'), value: localPublicCredential, onChange: (e) => {
                        if (publicCredentialFieldError !== '')
                            setLocalPublicCredentialFieldError('');
                        setLocalPublicCredential(e.target.value);
                    }, error: !!publicCredentialFieldError, errorText: publicCredentialFieldError })),
            React.createElement("div", { style: { marginTop: '20px' } },
                React.createElement(Button, { type: "submit", style: { width: '100%' }, id: "continueToPaymentBtn", "data-testid": "continue-to-payment-btn" }, localizedContent('continueToPayment')))),
        React.createElement("hr", { style: { marginTop: '30px' } }),
        React.createElement("div", { style: { marginTop: '50px', fontSize: '18px' } },
            React.createElement("span", { style: { marginRight: '10px' } }, localizedContent('alreadyHaveAccount')),
            React.createElement(Link, { href: "#", className: "secondaryLink", id: "loginLink", "data-testid": "login-link", onClick: (e) => {
                    e.preventDefault();
                    logger.track('aae_login_clicked');
                    setPublicCredential('');
                    setView(PUBLIC_CREDENTIAL);
                } }, localizedContent('logIn')))));
};
